import { Alert, Button, DatePicker, Form, Select } from 'antd';
import FileExcelOutlined from '@ant-design/icons/FileExcelOutlined';
import type { useContractImport } from './useContractImport';
import { ImportHelp } from './ImportHelp';
import styles from './UploadContracts.module.scss';

type Props = {
  flow: ReturnType<typeof useContractImport>;
  onShowPrevious: () => void;
};
export const ImportForm = ({ flow, onShowPrevious }: Props) => (
  <div className={styles.formLayout}>
    <section className={styles.card}>
      <h2>
        {flow.previous
          ? 'Subir archivo corregido'
          : 'Selecciona el archivo de contratos'}
      </h2>
      <p>
        Todos los registros se cargarán con el tipo de contrato seleccionado.
      </p>
      {flow.kindsStatus === 'error' && (
        <Alert
          type="error"
          showIcon
          title="No pudimos cargar los tipos de contrato."
          action={<Button onClick={flow.retryKinds}>Reintentar</Button>}
        />
      )}
      {flow.kindsStatus === 'ready' && flow.contractKinds.length === 0 && (
        <Alert
          type="warning"
          showIcon
          title="No hay tipos de contrato activos disponibles."
        />
      )}
      <Form
        form={flow.form}
        layout="vertical"
        onFinish={flow.submit}
        disabled={flow.isBusy}
      >
        <Form.Item
          label="Tipo de contrato"
          name="contractKind"
          rules={[
            { required: true, message: 'Selecciona el tipo de contrato' },
          ]}
        >
          <Select
            id="contractKindId"
            placeholder="Seleccionar tipo de contrato"
            loading={flow.kindsStatus === 'loading'}
            disabled={flow.isBusy || flow.kindsStatus !== 'ready'}
            options={flow.contractKinds.map((kind) => ({
              value: kind._id,
              label: kind.name,
            }))}
          />
        </Form.Item>
        <div className={styles.dates}>
          <Form.Item
            label="Fecha de inicio"
            name="startDate"
            rules={[
              { required: true, message: 'Selecciona la fecha de inicio' },
            ]}
          >
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>
          {flow.isTemporal && (
            <Form.Item
              label="Fecha de fin"
              name="endDate"
              dependencies={['startDate']}
              rules={[
                { required: true, message: 'Selecciona la fecha de fin' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue('startDate');
                    return !value || !start || !value.isBefore(start, 'day')
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            'La fecha de fin no puede ser anterior al inicio.'
                          )
                        );
                  },
                }),
              ]}
            >
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
          )}
        </div>
        <div
          className={styles.filePicker}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            const file = event.dataTransfer.files[0];
            if (file) flow.selectFile(file);
          }}
        >
          <FileExcelOutlined aria-hidden="true" />
          <strong>
            {flow.file?.name ?? 'Selecciona o arrastra tu archivo'}
          </strong>
          <p>
            {flow.previous && flow.file
              ? 'Nueva selección · aún no se ha validado. Puede tener el mismo nombre.'
              : 'Archivos .xlsx, .xls o .csv'}
          </p>
          <Button onClick={flow.openPicker} disabled={flow.isBusy}>
            {flow.file ? 'Cambiar archivo' : 'Seleccionar archivo'}
          </Button>
        </div>
        <Button
          type="primary"
          htmlType="submit"
          disabled={
            !flow.file ||
            flow.kindsStatus !== 'ready' ||
            !flow.contractKinds.length ||
            flow.isBusy
          }
          loading={flow.isBusy}
          data-cy="contract-import-submit"
        >
          {flow.previous ? 'Subir archivo corregido' : 'Subir archivo'}
        </Button>
      </Form>
      {flow.previous && (
        <div className={styles.previous}>
          <h3>Resultado anterior · antes de esta selección</h3>
          <p>
            {flow.previous.context.fileName}: {flow.previous.findings.length}{' '}
            errores. La carga anterior no guardó ningún registro.
          </p>
          <Button id="previous-import-errors" onClick={onShowPrevious}>
            Ver los {flow.previous.findings.length} errores anteriores
          </Button>
        </div>
      )}
    </section>
    <ImportHelp />
  </div>
);
